<script setup>
import { computed, ref } from 'vue';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  allAttachments: { type: Array, default: () => [] },
  isFetching: { type: Boolean, default: false },
});

const show = ref(false);
const isImageError = ref(false);
const attachment = ref(null);

const ALLOWED_FILE_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  IG_REEL: 'ig_reel',
};

const mediaFileTypes = Object.values(ALLOWED_FILE_TYPES);

const sortedAttachments = computed(() => {
  return [...props.allAttachments].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
});

const filteredCurrentChatAttachments = computed(() => {
  return sortedAttachments.value.filter(item =>
    mediaFileTypes.includes(item.file_type)
  );
});

const hasAttachments = computed(() => sortedAttachments.value.length > 0);

const onClick = item => {
  if (!mediaFileTypes.includes(item.file_type)) {
    window.open(item.data_url, '_blank');
    return;
  }

  attachment.value = item;
  show.value = true;
};

const onClose = () => {
  show.value = false;
};

const onImgError = () => {
  isImageError.value = true;
};
</script>

<template>
  <div
    v-if="props.isFetching"
    class="flex items-center justify-center py-8 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div v-else-if="hasAttachments" class="gallery">
    <div
      v-for="sortedAttachment in sortedAttachments"
      :key="sortedAttachment.message_id"
      class="thumb"
      @click="onClick(sortedAttachment)"
    >
      <div
        v-if="
          sortedAttachment.thumb_url &&
          mediaFileTypes.includes(sortedAttachment.file_type)
        "
      >
        <img :src="sortedAttachment.thumb_url" @error="onImgError" />
      </div>
      <div
        v-else-if="sortedAttachment.file_type === ALLOWED_FILE_TYPES.AUDIO"
        class="audio-thumb"
      >
        <span class="i-lucide-headphones audio-icon" />
      </div>
      <div v-else class="file-thumb">
        <span class="i-lucide-file-text file-icon" />
      </div>
    </div>

    <GalleryView
      v-if="show && attachment"
      v-model:show="show"
      :attachment="attachment"
      :all-attachments="filteredCurrentChatAttachments"
      @error="onImgError"
      @close="onClose"
    />
  </div>
  <div v-else class="empty-state" />
</template>

<style lang="scss" scoped>
.gallery {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
}

.thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #111;
  overflow: hidden;
  cursor: pointer;
}

.thumb img,
.thumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.doc,
.link {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: #1f2a30;
  color: white;
  font-size: 20px;
}

.overlay {
  position: absolute;
  color: white;
  font-size: 11px;
}

.bottom-right {
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6);
  padding: 2px 5px;
  border-radius: 4px;
}

.bottom-left {
  bottom: 4px;
  left: 4px;
}

.badge {
  background: red;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: bold;
}

.audio-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #1a2a35;
}

.audio-icon {
  width: 48px;
  height: 48px;
  color: #a0b4bf;
}

.file-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #1a2a35;
}

.file-icon {
  width: 48px;
  height: 48px;
  color: #a0b4bf;
}

.empty-state {
  min-height: 1px;
}
</style>
